import React, { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";
import blue from "@material-ui/core/colors/blue";
import green from "@material-ui/core/colors/green";
import grey from "@material-ui/core/colors/grey";
import {
  SortableContainer,
  SortableHandle,
  SortableElement,
} from "react-sortable-hoc";
import arrayMove from "array-move";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { treinoExercicioApi } from "../../services/Api";
import Fab from "@material-ui/core/Fab";
import AddIcon from "@material-ui/icons/Add";
import { Snackbar } from "elentari";

const styles = (theme) => ({
  listContainer: {
    padding: theme.spacing.unit * 3,
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  item: {
    width: "100%",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 5,
    overflow: "hidden",
    backgroundColor: theme.palette.common.white,
  },
  cardContent: {
    cursor: "grab",
  },
  itemContent: {
    padding: theme.spacing.unit * 3,
  },
  repeticao: {
    marginTop: 50,
    minWidth: 130,
  },
  colorfullBar: {
    position: "absolute",
    height: 200,
    width: 8,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing.unit,
  },
  fab: {
    position: "fixed",
    right: 4 * theme.spacing.unit,
    bottom: 4 * theme.spacing.unit,
  },
  colorfullBarContainer: {
    position: "relative",
  },
});

const ColorfullBar = ({ classes, color }) => (
  <div className={classes.colorfullBarContainer}>
    <div className={classes.colorfullBar} style={{ backgroundColor: color }} />
  </div>
);

const CardContent = SortableHandle(({ value, classes }) => (
  <Grid container spacing={32} className={classes.cardContent}>
    <Grid item xs>
      <Typography color="textSecondary">Exercício</Typography>
      <Typography variant="body2" gutterBottom>
        {value.exercicio.nome || "-"}
      </Typography>
    </Grid>
    <Grid item>
      <Typography color="textSecondary">Série</Typography>
      <Typography variant="body2" gutterBottom>
        {value.series || "-"}
      </Typography>
    </Grid>
    <Grid item>
      <Typography color="textSecondary">Repetição</Typography>
      <Typography variant="body2" gutterBottom>
        {value.repeticoes || "-"}
      </Typography>
    </Grid>
    <Grid item>
      <Typography color="textSecondary">Peso (Kg)</Typography>
      <Typography variant="body2" gutterBottom>
        {value.peso || "-"}
      </Typography>
    </Grid>
    <Grid item>
      <Typography color="textSecondary">Descanso (segundos)</Typography>
      <Typography variant="body2" gutterBottom>
        {value.descanso || "-"}
      </Typography>
    </Grid>
  </Grid>
));

const Card = SortableElement(
  ({
    value,
    onCombineClick,
    isFirst,
    remove,
    onClickEdit,
    addMarginTop,
    classes,
  }) => (
    <div
      className={classes.item}
      style={{
        borderTop: value.combinado ? 0 : `1px solid ${grey[300]}`,
        marginTop: !value.combinado && addMarginTop ? 18 : 0,
      }}
    >
      <ColorfullBar classes={classes} color={value.group} />
      <div className={classes.itemContent}>
        <Grid container spacing={32}>
          <Grid item sm>
            <CardContent classes={classes} value={value} />
          </Grid>
          <Grid item style={{ marginTop: 15 }}>
            <Button size="small" onClick={remove}>
              Excluir
            </Button>
            <Button size="small" onClick={onClickEdit}>
              Editar
            </Button>
            <Button
              disabled={isFirst}
              style={{ width: 210 }}
              onClick={onCombineClick}
              size="small"
              color="primary"
            >
              {value.combinado
                ? "Descombinar com Anterior"
                : "Combinar com Anterior"}
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  )
);

const SortableList = SortableContainer(
  ({
    items,
    onCombineClick,
    undo,
    clearRemoved,
    removedItem,
    handleDelete,
    grabbing,
    classes,
    onClickNew,
    onClickEdit,
  }) => {
    return (
      <div
        className={classes.listContainer}
        style={{ cursor: grabbing && "grabbing" }}
      >
        {items.map((value, index) => (
          <Card
            addMarginTop={index > 0}
            isFirst={index === 0}
            remove={() => handleDelete(value)}
            onCombineClick={() => onCombineClick(index, value)}
            onClickEdit={() => onClickEdit(value)}
            key={`item-${index}`}
            index={index}
            classes={classes}
            value={value}
          />
        ))}
        <Fab
          color="primary"
          aria-label="Add"
          className={classes.fab}
          onClick={onClickNew}
        >
          <AddIcon />
        </Fab>
        <Snackbar
          message={removedItem ? "Removido com Sucesso! Deseja desfazer?" : ""}
          action={[
            <Button key="undo" color="secondary" size="small" onClick={undo}>
              DESFAZER
            </Button>,
          ]}
          onClose={clearRemoved}
          autoHideDuration={6000}
        />
      </div>
    );
  }
);

const SerieList = ({ classes, match, onClickNew, onClickEdit }) => {
  const [items, setItems] = useState([]);
  const [grabbing, setGrabbing] = useState(false);
  const [removedItem, setRemoved] = useState(null);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    let newItems = arrayMove(items, oldIndex, newIndex);
    if (newIndex === 0) {
      newItems[0].combinado = false;
    }
    newItems.forEach((item, index) => (item.ordem = index));
    setGrabbing(false);
    newItems = updateCombinationsColors(newItems);
    setItems(newItems);
    updateItemsOnApi(newItems);
  };
  const onSortStart = () => setGrabbing(true);

  const fetchTreinoExercicios = async () => {
    const response = await treinoExercicioApi.getList({
      where: { treinoId: match.params.id },
      order: "ordem asc",
    });
    if (response.ok) {
      setItems(updateCombinationsColors(response.data));
    }
  };

  const clearRemoved = () => setRemoved(null);
  const undo = async () => {
    await treinoExercicioApi.undoRemove(removedItem.id);
    await fetchTreinoExercicios();
    setRemoved(null);
  };
  const handleDelete = async (item) => {
    const response = await treinoExercicioApi.remove(item.id);
    if (response.ok) {
      await fetchTreinoExercicios();
      setRemoved(item);
    }
  };

  const updateCombinationsColors = (items) => {
    const newItems = [...items];
    let group = green[300];
    let colorUsed = false;
    newItems.forEach((i) => (i.group = grey[300]));
    for (let i = 1; i < newItems.length; i++) {
      let previous = newItems[i - 1];
      let current = newItems[i];
      if (current.combinado) {
        previous = { ...previous, group };
        current = { ...current, group };
        newItems[i - 1] = previous;
        newItems[i] = current;
        colorUsed = true;
      } else {
        if (colorUsed) {
          group = group === blue[300] ? green[300] : blue[300];
          colorUsed = false;
        }
        current = { ...current, group: grey[300] };
        newItems[i] = current;
      }
    }
    return newItems;
  };

  const updateItemsOnApi = (items) => {
    return Promise.all(
      items.map((item) => {
        let newItem = {
          ...item,
          exercicioId: item.exercicio.id,
        };
        delete newItem.group;
        delete newItem.exercicio;
        return treinoExercicioApi.update(item.id, newItem);
      })
    );
  };

  const toggleCombined = (index, item) => {
    let newItems = [...items];
    newItems[index] = {
      ...item,
      combinado: !item.combinado,
    };
    newItems = updateCombinationsColors(newItems);
    setItems(newItems);
    updateItemsOnApi(newItems);
  };

  useEffect(() => {
    setTimeout(() => {
      fetchTreinoExercicios();
    }, 200);
  }, []);

  return (
    <SortableList
      classes={classes}
      onClickNew={onClickNew}
      useDragHandle
      undo={undo}
      clearRemoved={clearRemoved}
      onCombineClick={toggleCombined}
      removedItem={removedItem}
      handleDelete={handleDelete}
      onClickEdit={onClickEdit}
      items={items}
      grabbing={grabbing}
      onSortEnd={onSortEnd}
      onSortStart={onSortStart}
    />
  );
};

export default withStyles(styles)(SerieList);
